import React, { useState, useEffect } from 'react';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import { apiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { addToast } = useToast();

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      // Admin curated opportunities
      const oppRes = await apiClient.get('/opportunities');
      
      // Fetch company jobs through a custom admin route if available, otherwise just opps
      // For now we'll display what's available from opportunities
      setJobs(oppRes.data || []);
    } catch (err) {
      addToast('Failed to load jobs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    
    try {
      await apiClient.delete(`/opportunities/${id}`);
      addToast('Job deleted successfully', 'success');
      setJobs(jobs.filter(j => j.id !== id));
    } catch (err) {
      addToast('Failed to delete job', 'error');
    }
  };

  const filtered = jobs.filter(j => {
    return (j.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
           (j.company_name || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="admin-page-title">Jobs & Opportunities</h1>
          <p className="admin-page-sub">View all active job postings on the platform.</p>
        </div>
      </div>

      <div className="card-elevated">
        <div className="flex justify-between items-center mb-6">
          <div className="search-bar w-64">
            <span className="search-icon text-sm">🔍</span>
            <input 
              type="text" 
              placeholder="Search by title or company..." 
              className="text-sm py-2"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No jobs found matching your criteria.</div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Job Title</th>
                  <th>Company</th>
                  <th>Type & Location</th>
                  <th>Deadline</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(job => (
                  <tr key={job.id}>
                    <td>
                      <div className="font-semi text-gray-900">{job.title}</div>
                      <div className="flex gap-1 mt-1">
                        {job.skills?.split(',').slice(0, 2).map((skill, i) => (
                          <Badge key={i} variant="gray" className="!text-[10px] !py-0.5 !px-1.5">{skill.trim()}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="text-sm font-medium">{job.company_name}</td>
                    <td>
                      <Badge variant="primary-light" className="mb-1">{job.employment_type || 'Job'}</Badge>
                      <div className="text-xs text-gray-500">{job.location}</div>
                    </td>
                    <td className="text-sm">{new Date(job.deadline).toLocaleDateString()}</td>
                    <td>
                      <Button size="sm" variant="ghost" className="!py-1 !px-2 text-xs text-danger" onClick={() => handleDelete(job.id)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminJobs;
